import type { GameId, PlayerId } from '../../shared/types/ids';
import type { GameProgressSnapshot } from '../../shared/types/progress';
import type { ProgressRepository } from '../interfaces/repositories';
import { projectProgressTable } from './governed-records';
import type { JsonStore } from './json-store';
import { progressKey, STORAGE_KEYS } from './keys';
import { toGameProgressDto } from './persistence-dto';

type ProgressTable = Record<string, GameProgressSnapshot>;

export function createLocalProgressRepository(store: JsonStore): ProgressRepository {
  const readTable = (): ProgressTable =>
    store.read(STORAGE_KEYS.progress, projectProgressTable) ?? {};

  return {
    async getGameProgress(
      playerId: PlayerId,
      gameId: GameId,
    ): Promise<GameProgressSnapshot | null> {
      return readTable()[progressKey(playerId, gameId)] ?? null;
    },

    async saveGameProgress(
      playerId: PlayerId,
      gameId: GameId,
      progress: GameProgressSnapshot,
    ): Promise<void> {
      /**
       * Counter *keys* are registered as well as their values being numbers, so neither a media
       * string nor a coordinate name smuggled in as a "counter" has anywhere to land (S1-2).
       *
       * The row is filed under `<player>::<game>` and is rebuilt to say so itself, because the
       * read side now refuses a row that disagrees with its key (S1-6). Written from the
       * arguments rather than trusted from the module's returned object: the caller asking to
       * save *this* child's progress for *this* game is the claim the table is keyed on.
       */
      const projected = toGameProgressDto({ ...progress, playerId, gameId });
      if (projected === null) return;

      const table = readTable();
      table[progressKey(playerId, gameId)] = projected;
      store.write(STORAGE_KEYS.progress, table);
    },
  };
}
